import { useState } from'react';
import { motion, AnimatePresence } from'framer-motion';
import { BookOpen, MapPin, Calendar, Eye, Wind, Flame, Cloud } from'lucide-react';
import { biblicalReferences } from'../../data/frameworks';

export default function Step1() {
 const [selectedElement, setSelectedElement] = useState<number | null>(null);

 const visionElements = [
 {
 icon: Wind,
 title:'The Storm Wind',
 hebrew:'רוח סערה',
 transliteration:'Ruach Se\'arah',
 description:'A great wind from the north, carrying divine presence and transformative power.',
 color:'#7C3AED',
 details:'The storm wind represents the initial stirring of divine revelation, coming from the north - the direction of mystery and hiddenness in biblical symbolism.'
 },
 {
 icon: Cloud,
 title:'The Great Cloud',
 hebrew:'ענן גדול',
 transliteration:'Anan Gadol',
 description:'A massive cloud with brightness around it, representing the concealing veil of divine mystery.',
 color:'#6B7280',
 details:'The cloud simultaneously conceals and reveals - it hides the direct divine light while itself shining with reflected glory.'
 },
 {
 icon: Flame,
 title:'The Flashing Fire',
 hebrew:'אש מתלקחת',
 transliteration:'Esh Mitlakahat',
 description:'Fire folding in upon itself, the dynamic energy of divine revelation.',
 color:'#EA580C',
 details:'Self-consuming fire that burns without destruction represents the eternal nature of divine energy - always active, never depleted.'
 },
 {
 icon: Eye,
 title:'The Amber Glow',
 hebrew:'חשמל',
 transliteration:'Hashmal',
 description:'Hashmal - the mysterious electrum-like substance at the heart of the fire.',
 color:'#FBBF24',
 details:'Hashmal is one of the most mysterious words in Hebrew scripture. Later tradition interpreted it as a combination of"speaking" and"silence" - divine communication beyond words.'
 },
 ];

 return (
 <div className="section-container">
 {/* Vision Introduction Card */}
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 className="relative mb-16"
 >
 <div className="vision-container min-h-[400px] md:min-h-[500px] relative flex flex-col items-center justify-center p-8">
 {/* Decorative background elements */}
 <div className="absolute inset-0 bg-gradient-to-b from-violet-900/20 via-orange-600/10 to-amber-500/20 rounded-2xl" />

 {/* Content */}
 <div className="relative z-10 text-center max-w-2xl">
 <motion.div
 initial={{ opacity: 0, scale: 0.9 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ delay: 0.2 }}
 className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-violet-600 flex items-center justify-center"
 >
 <Flame className="w-12 h-12 text-white" />
 </motion.div>

 <motion.p
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.3 }}
 className="hebrew-text text-2xl md:text-3xl kedushah-text mb-4"
 >
 ואראה והנה רוח סערה באה מן הצפון
 </motion.p>
 <motion.p
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ delay: 0.5 }}
 className="text-lg md:text-xl text-white/80 italic max-w-xl mx-auto"
 >
"And I looked, and behold, a stormy wind came from the north..."
 </motion.p>
 <motion.p
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ delay: 0.7 }}
 className="text-sm text-white/60 mt-2"
 >
 - Ezekiel 1:4
 </motion.p>
 </div>
 </div>
 </motion.div>

 {/* Introduction */}
 <div className="max-w-4xl mx-auto mb-16">
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.2 }}
 className="prose prose-lg dark:prose-invert mx-auto"
 >
 <p>
 The vision of the Merkavah (Divine Chariot) is one of the most profound and mysterious
 passages in all of Jewish scripture. It appears in the first chapter of the Book of Ezekiel,
 describing an overwhelming encounter with the divine presence during the prophet's exile
 in Babylon.
 </p>
 <p>
 This vision became the foundation for an entire mystical tradition—Ma'aseh Merkavah
 (the Work of the Chariot)—which influenced Jewish mysticism for millennia and contributed
 to the development of Kabbalah.
 </p>
 </motion.div>
 </div>

 {/* Historical Context */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.3 }}
 className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
 >
 <div className="card transition-all duration-300 hover:shadow-lg hover:border-primary-500/50">
 <div className="flex items-center gap-3 mb-4">
 <div className="p-2 rounded-lg bg-primary-600/10">
 <Calendar className="w-6 h-6 text-primary-600" />
 </div>
 <h3 className="text-lg font-semibold">The Time</h3>
 </div>
 <p className="text-[var(--color-muted)]">
 593 BCE, during the fifth year of King Jehoiachin's exile to Babylon. The Temple in
 Jerusalem still stood, but dark times loomed.
 </p>
 </div>

 <div className="card transition-all duration-300 hover:shadow-lg hover:border-primary-500/50">
 <div className="flex items-center gap-3 mb-4">
 <div className="p-2 rounded-lg bg-primary-600/10">
 <MapPin className="w-6 h-6 text-primary-600" />
 </div>
 <h3 className="text-lg font-semibold">The Place</h3>
 </div>
 <p className="text-[var(--color-muted)]">
 By the River Chebar (Kebar) in Babylon, among the Jewish exiles. Far from the Temple,
 yet the heavens opened.
 </p>
 </div>

 <div className="card transition-all duration-300 hover:shadow-lg hover:border-primary-500/50">
 <div className="flex items-center gap-3 mb-4">
 <div className="p-2 rounded-lg bg-primary-600/10">
 <BookOpen className="w-6 h-6 text-primary-600" />
 </div>
 <h3 className="text-lg font-semibold">The Prophet</h3>
 </div>
 <p className="text-[var(--color-muted)]">
 Ezekiel son of Buzi, a priest. At thirty years old—when he would have begun Temple
 service—the heavens opened instead.
 </p>
 </div>
 </motion.div>

 {/* Interactive Vision Elements */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.4 }}
 className="mb-16"
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">
 The <span className="text-flame">Elements</span> of the Vision
 </h2>
 <p className="text-center text-[var(--color-muted)] mb-8 max-w-2xl mx-auto">
 Click each element to learn more about its mystical significance
 </p>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 {visionElements.map((element, index) => (
 <motion.div
 key={element.title}
 initial={{ opacity: 0, scale: 0.95 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ delay: 0.5 + index * 0.1 }}
 >
 <button
 onClick={() => setSelectedElement(selectedElement === index ? null : index)}
 className={`w-full text-left card group transition-all duration-300 hover:shadow-lg ${
 selectedElement === index ?'ring-2 ring-primary-500' :''
 }`}
 >
 <div className="flex items-start gap-4">
 <div
 className="p-3 rounded-xl transition-all duration-300 group-hover:scale-110"
 style={{
 backgroundColor: `${element.color}20`,
 }}
 >
 <element.icon
 className="w-8 h-8 transition-colors"
 style={{ color: element.color }}
 />
 </div>
 <div className="flex-1">
 <h3 className="text-xl font-semibold mb-1">{element.title}</h3>
 <div className="flex items-center gap-2 mb-2">
 <p className="hebrew-text text-lg" style={{ color: element.color }}>
 {element.hebrew}
 </p>
 <span className="text-sm text-[var(--color-muted)] italic">
 ({element.transliteration})
 </span>
 </div>
 <p className="text-[var(--color-muted)]">{element.description}</p>
 </div>
 </div>
 </button>

 <AnimatePresence>
 {selectedElement === index && (
 <motion.div
 initial={{ opacity: 0, height: 0 }}
 animate={{ opacity: 1, height:'auto' }}
 exit={{ opacity: 0, height: 0 }}
 className="overflow-hidden"
 >
 <div
 className="mt-2 p-4 rounded-xl border-l-4"
 style={{
 backgroundColor: `${element.color}10`,
 borderColor: element.color,
 }}
 >
 <p className="text-sm text-[var(--color-muted)]">{element.details}</p>
 </div>
 </motion.div>
 )}
 </AnimatePresence>
 </motion.div>
 ))}
 </div>
 </motion.div>

 {/* Biblical References */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.6 }}
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">
 Key <span className="text-[#202122] font-bold font-serif">Biblical Sources</span>
 </h2>

 <div className="overflow-x-auto">
 <table className="w-full">
 <thead>
 <tr className="border-b border-[var(--color-border)]">
 <th className="text-left py-4 px-4 font-semibold">Reference</th>
 <th className="text-left py-4 px-4 font-semibold">Title</th>
 <th className="text-left py-4 px-4 font-semibold hidden md:table-cell">Summary</th>
 </tr>
 </thead>
 <tbody>
 {biblicalReferences.map((ref, index) => (
 <motion.tr
 key={index}
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ delay: 0.7 + index * 0.05 }}
 className="border-b border-[var(--color-border)] hover:bg-[var(--color-card)] transition-all duration-300"
 >
 <td className="py-4 px-4">
 <span className="font-medium text-primary-600 dark:text-primary-400">
 {ref.book} {ref.chapter}:{ref.verses}
 </span>
 </td>
 <td className="py-4 px-4">{ref.title}</td>
 <td className="py-4 px-4 text-[var(--color-muted)] hidden md:table-cell">
 {ref.summary}
 </td>
 </motion.tr>
 ))}
 </tbody>
 </table>
 </div>
 </motion.div>

 {/* Significance Box */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.8 }}
 className="mt-16 p-8 rounded-2xl bg-gradient-to-r from-primary-600/10 to-secondary-700/10 border border-primary-600/20"
 >
 <h3 className="text-xl font-semibold mb-4 text-center">Why This Vision Matters</h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-[var(--color-muted)]">
 <div>
 <h4 className="font-semibold text-[var(--color-foreground)] mb-2">Theological Significance</h4>
 <p>
 The vision demonstrates that divine presence is not confined to the Temple in Jerusalem.
 Even in exile, even in a foreign land, the heavens can open and God can be encountered.
 </p>
 </div>
 <div>
 <h4 className="font-semibold text-[var(--color-foreground)] mb-2">Mystical Foundation</h4>
 <p>
 This passage became the foundation for Jewish mystical practice—the contemplation of the
 chariot was considered the highest form of spiritual study in early Judaism.
 </p>
 </div>
 </div>
 </motion.div>
 </div>
 );
}
